import React, { useEffect, useMemo, useRef, useState } from 'react';

import './QuestionPlayer.css';
import { getRuntimeConfigValue } from './runtimeConfig';

export type SupportedLanguage = 'pl' | 'en' | 'de' | 'ua';
export type QuestionScope = 'PODSTAWOWY' | 'SPECJALISTYCZNY';
export type QuestionType = 'TAK_NIE' | 'ABC';
export type MediaType = 'VIDEO' | 'IMAGE';
type PlayerState = 'LOCKED' | 'PLAYING' | 'ANSWER_COUNTDOWN' | 'ANSWER_TIME_OVER';

export type QuestionPlayerQuestion = {
  id: string;
  number: number;
  type: QuestionType;
  scope: QuestionScope;
  points: number;
  media_file: string | null;
  media_type: MediaType | null;
  language: SupportedLanguage;
  question_text: string;
  answer_a?: string | null;
  answer_b?: string | null;
  answer_c?: string | null;
};

type UiCopy = {
  scopeBasic: string;
  scopeSpecialist: string;
  questionLabel: string;
  pointsLabel: string;
  basicHint: string;
  specialistHint: string;
  playerReady: string;
  lockedLabel: string;
  skipLabel: string;
  countdownPrefix: string;
  countdownSuffix: string;
  watchComplete: string;
  answerTimeOver: string;
  mediaUnavailable: string;
  answerYes: string;
  answerNo: string;
  playLabel: string;
};

type QuestionPlayerProps = {
  question: QuestionPlayerQuestion;
  language: SupportedLanguage;
  questionOrder: number;
  totalQuestions: number;
  answeredCount: number;
  remainingCount: number;
  examTimeLeft: number;
  examTimeLabel: string;
  answeredLabel: string;
  remainingLabel: string;
  nextButtonLabel: string;
  nextButtonDisabled: boolean;
  showSidebar?: boolean;
  onNextQuestion?: () => void;
  onAnswerSelect?: (answer: 'T' | 'N' | 'A' | 'B' | 'C') => void;
  onTimeExpired?: () => void;
};

const MEDIA_BASE_URL = (getRuntimeConfigValue('REACT_APP_MEDIA_BASE_URL') || 'https://pub-2cdeeb37268f46c7b8d1013e3ab56448.r2.dev')
  .replace(/\/+$/, '');
const BASIC_LOCK_SECONDS = 20;
const BASIC_ANSWER_SECONDS = 15;
const VIDEO_ANSWER_SECONDS = 15;
const SPECIALIST_ANSWER_SECONDS = 50;

const uiCopy: Record<SupportedLanguage, UiCopy> = {
  pl: {
    scopeBasic: 'PODSTAWOWE',
    scopeSpecialist: 'SPECJALISTYCZNE',
    questionLabel: 'Pytanie',
    pointsLabel: 'Punkty',
    basicHint: 'W części podstawowej najpierw jest do 20 sekund na zapoznanie się z pytaniem. Ten etap można zakończyć kliknięciem.',
    specialistHint: 'W części specjalistycznej nie ma blokady startowej. Od razu biegnie 50 sekund na odpowiedź.',
    playerReady: 'Media są gotowe do odtworzenia.',
    lockedLabel: 'Czas na zapoznanie się z pytaniem',
    skipLabel: 'Kliknij, aby zakończyć ten etap wcześniej',
    countdownPrefix: 'Pozostały czas na odpowiedź',
    countdownSuffix: 'sek.',
    watchComplete: 'Film został odtworzony. Odpowiedz przed końcem czasu.',
    answerTimeOver: 'Czas na odpowiedź minął.',
    mediaUnavailable: 'To pytanie nie ma poprawnych danych mediów do wyświetlenia.',
    answerYes: 'Tak',
    answerNo: 'Nie',
    playLabel: 'Start',
  },
  en: {
    scopeBasic: 'BASIC',
    scopeSpecialist: 'SPECIALIST',
    questionLabel: 'Question',
    pointsLabel: 'Points',
    basicHint: 'In the basic section the player is locked for up to 20 seconds. The user can end that stage by clicking.',
    specialistHint: 'In the specialist section there is no start lock. The 50-second answer timer begins immediately.',
    playerReady: 'The media is ready to play.',
    lockedLabel: 'Time to review the question',
    skipLabel: 'Click to end this stage earlier',
    countdownPrefix: 'Time left to answer',
    countdownSuffix: 'sec.',
    watchComplete: 'The video has finished. Answer before time runs out.',
    answerTimeOver: 'Answer time is over.',
    mediaUnavailable: 'This question does not have valid media to display.',
    answerYes: 'Yes',
    answerNo: 'No',
    playLabel: 'Start',
  },
  de: {
    scopeBasic: 'GRUNDWISSEN',
    scopeSpecialist: 'SPEZIALWISSEN',
    questionLabel: 'Frage',
    pointsLabel: 'Punkte',
    basicHint: 'Im Grundwissen bleibt der Player bis zu 20 Sekunden gesperrt. Dieser Schritt kann per Klick beendet werden.',
    specialistHint: 'Im Spezialwissen gibt es keine Startblockade. Die 50 Sekunden für die Antwort laufen sofort.',
    playerReady: 'Das Medium ist bereit zur Wiedergabe.',
    lockedLabel: 'Zeit, um die Frage zu lesen',
    skipLabel: 'Klicken, um diese Phase früher zu beenden',
    countdownPrefix: 'Verbleibende Antwortzeit',
    countdownSuffix: 'Sek.',
    watchComplete: 'Das Video ist beendet. Antworte vor Ablauf der Zeit.',
    answerTimeOver: 'Die Antwortzeit ist abgelaufen.',
    mediaUnavailable: 'Diese Frage hat keine gültigen Medien zur Anzeige.',
    answerYes: 'Ja',
    answerNo: 'Nein',
    playLabel: 'Start',
  },
  ua: {
    scopeBasic: 'БАЗОВІ',
    scopeSpecialist: 'СПЕЦІАЛІЗОВАНІ',
    questionLabel: 'Запитання',
    pointsLabel: 'Бали',
    basicHint: 'У базовій частині плеєр заблокований максимум на 20 секунд. Цей етап можна завершити натисканням.',
    specialistHint: 'У спеціалізованій частині стартового блокування немає. Відлік 50 секунд починається одразу.',
    playerReady: 'Медіа готове до відтворення.',
    lockedLabel: 'Час ознайомитися із запитанням',
    skipLabel: 'Натисніть, щоб завершити цей етап раніше',
    countdownPrefix: 'Залишок часу на відповідь',
    countdownSuffix: 'с.',
    watchComplete: 'Відео завершено. Дайте відповідь до завершення часу.',
    answerTimeOver: 'Час на відповідь минув.',
    mediaUnavailable: 'Для цього запитання немає коректних медіаданих.',
    answerYes: 'Так',
    answerNo: 'Ні',
    playLabel: 'Старт',
  },
};

function formatTime(seconds: number) {
  const safe = Math.max(0, seconds);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : String(secs);
}

function inferMediaType(question: QuestionPlayerQuestion): MediaType | null {
  if (!question.media_file) {
    return null;
  }
  if (question.media_type === 'VIDEO' || question.media_type === 'IMAGE') {
    return question.media_type;
  }

  const normalized = question.media_file.toLowerCase();
  if (
    normalized.endsWith('.jpg') ||
    normalized.endsWith('.jpeg') ||
    normalized.endsWith('.png') ||
    normalized.endsWith('.gif') ||
    normalized.endsWith('.webp')
  ) {
    return 'IMAGE';
  }
  return 'VIDEO';
}

function buildMediaUrl(mediaFile: string | null) {
  if (!mediaFile) {
    return '';
  }

  const normalizedMediaFile = mediaFile.replace(/\.wmv$/i, '.mp4');
  return `${MEDIA_BASE_URL}/videos/${normalizedMediaFile}`;
}

export function QuestionPlayer({
  question,
  language,
  questionOrder,
  totalQuestions,
  answeredCount,
  remainingCount,
  examTimeLeft,
  examTimeLabel,
  answeredLabel,
  remainingLabel,
  nextButtonLabel,
  nextButtonDisabled,
  showSidebar = true,
  onNextQuestion,
  onAnswerSelect,
  onTimeExpired,
}: QuestionPlayerProps) {
  const [playerState, setPlayerState] = useState<PlayerState>('LOCKED');
  const [previewSecondsLeft, setPreviewSecondsLeft] = useState(BASIC_LOCK_SECONDS);
  const [answerSecondsLeft, setAnswerSecondsLeft] = useState(BASIC_ANSWER_SECONDS);
  const [selectedAnswer, setSelectedAnswer] = useState<'T' | 'N' | 'A' | 'B' | 'C' | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timeoutReportedRef = useRef(false);
  const copy = uiCopy[language];
  const mediaType = inferMediaType(question);
  const isVideo = mediaType === 'VIDEO';
  const mediaUrl = buildMediaUrl(question.media_file);
  const answerTimerEnabled = playerState === 'ANSWER_COUNTDOWN';
  const answerControlsEnabled = playerState === 'ANSWER_COUNTDOWN';

  useEffect(() => {
    setSelectedAnswer(null);
    timeoutReportedRef.current = false;

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    if (question.scope === 'PODSTAWOWY') {
      setPlayerState('LOCKED');
      setPreviewSecondsLeft(BASIC_LOCK_SECONDS);
      setAnswerSecondsLeft(BASIC_ANSWER_SECONDS);
      return;
    }

    setPreviewSecondsLeft(0);
    setAnswerSecondsLeft(isVideo ? VIDEO_ANSWER_SECONDS : SPECIALIST_ANSWER_SECONDS);
    setPlayerState(isVideo ? 'PLAYING' : 'ANSWER_COUNTDOWN');
  }, [question.id, question.scope, isVideo]);

  useEffect(() => {
    if (playerState !== 'LOCKED') {
      return;
    }

    const timer = window.setInterval(() => {
      setPreviewSecondsLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          unlockPlayer();
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [playerState, question.id]);

  useEffect(() => {
    if (!answerTimerEnabled) {
      return;
    }

    const timer = window.setInterval(() => {
      setAnswerSecondsLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          setPlayerState('ANSWER_TIME_OVER');
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [answerTimerEnabled, question.id]);

  useEffect(() => {
    if (!isVideo || !videoRef.current || playerState !== 'PLAYING') {
      return;
    }

    videoRef.current.play().catch(() => undefined);
  }, [isVideo, playerState, question.id]);

  useEffect(() => {
    if (playerState !== 'ANSWER_TIME_OVER' || timeoutReportedRef.current) {
      return;
    }

    timeoutReportedRef.current = true;
    onTimeExpired?.();
  }, [onTimeExpired, playerState]);

  function unlockPlayer() {
    if (question.scope === 'PODSTAWOWY') {
      setPlayerState(isVideo ? 'PLAYING' : 'ANSWER_COUNTDOWN');
      if (!isVideo) {
        setAnswerSecondsLeft(BASIC_ANSWER_SECONDS);
      }
    }
  }

  function handleLockedOverlayClick() {
    if (playerState !== 'LOCKED') {
      return;
    }

    setPreviewSecondsLeft(0);
    unlockPlayer();
  }

  function handleVideoEnded() {
    if (question.scope === 'PODSTAWOWY') {
      if (playerState !== 'ANSWER_TIME_OVER') {
        setAnswerSecondsLeft(VIDEO_ANSWER_SECONDS);
        setPlayerState('ANSWER_COUNTDOWN');
      }
      return;
    }

    if (playerState !== 'ANSWER_TIME_OVER') {
      setAnswerSecondsLeft(VIDEO_ANSWER_SECONDS);
      setPlayerState('ANSWER_COUNTDOWN');
    }
  }

  function handleAnswerClick(answer: 'T' | 'N' | 'A' | 'B' | 'C') {
    if (!answerControlsEnabled) {
      return;
    }

    setSelectedAnswer(answer);
    onAnswerSelect?.(answer);
  }

  const answerOptions =
    question.type === 'TAK_NIE'
      ? [
          { value: 'T' as const, label: copy.answerYes },
          { value: 'N' as const, label: copy.answerNo },
        ]
      : [
          { value: 'A' as const, label: question.answer_a ?? '' },
          { value: 'B' as const, label: question.answer_b ?? '' },
          { value: 'C' as const, label: question.answer_c ?? '' },
        ];
  const timerValue = playerState === 'LOCKED' ? formatTime(previewSecondsLeft) : formatTime(answerSecondsLeft);

  return (
    <section className="question-player">
      <div className="question-player__meta">
        <div className="question-player__meta-badges">
          <span className="question-player__badge">
            {copy.questionLabel} {questionOrder}
          </span>
          <span className="question-player__badge question-player__badge--scope">
            {question.scope === 'PODSTAWOWY' ? copy.scopeBasic : copy.scopeSpecialist}
          </span>
          <span className="question-player__badge question-player__badge--points">
            {question.points} {copy.pointsLabel}
          </span>
        </div>

        <div className="question-player__timer">
          <div className="question-player__timer-label">
            {playerState === 'LOCKED' ? copy.lockedLabel : copy.countdownPrefix}
          </div>
          <div className="question-player__timer-value">{timerValue}</div>
        </div>
      </div>

      <div className="question-player__media-shell">
        {!mediaType || !mediaUrl ? (
          <div className="question-player__media-fallback">{copy.mediaUnavailable}</div>
        ) : isVideo ? (
          <video
            ref={videoRef}
            src={mediaUrl}
            controls={playerState !== 'LOCKED'}
            playsInline
            preload="metadata"
            onClick={() => {
              if (videoRef.current?.paused) {
                void videoRef.current.play().catch(() => undefined);
              }
            }}
            onEnded={handleVideoEnded}
            className={`question-player__media question-player__media--video ${playerState === 'LOCKED' ? 'question-player__media--locked' : ''}`}
          >
            Your browser does not support video playback.
          </video>
        ) : (
          <img
            src={mediaUrl}
            alt={question.question_text}
            className={`question-player__media question-player__media--image ${playerState === 'LOCKED' ? 'question-player__media--locked' : ''}`}
          />
        )}

        {playerState === 'LOCKED' ? (
          <button type="button" className="question-player__overlay" onClick={handleLockedOverlayClick}>
            <div>
              <div className="question-player__overlay-label">{copy.lockedLabel}</div>
              <div className="question-player__overlay-time">{formatTime(previewSecondsLeft)}</div>
              <div className="question-player__overlay-hint">{copy.skipLabel}</div>
            </div>
          </button>
        ) : null}

        {playerState === 'ANSWER_TIME_OVER' ? (
          <div className="question-player__expired">{copy.answerTimeOver}</div>
        ) : null}
      </div>

      {showSidebar ? (
        <div className="question-player__stats-bar">
          <div className="question-player__stat-item">
            <span className="question-player__stat-label">{examTimeLabel}</span>
            <span className="question-player__stat-value">{formatTime(examTimeLeft)}</span>
          </div>
          <div className="question-player__stat-item">
            <span className="question-player__stat-label">{copy.questionLabel}</span>
            <span className="question-player__stat-value">{questionOrder}/{totalQuestions}</span>
          </div>
          <div className="question-player__stat-item">
            <span className="question-player__stat-label">{copy.pointsLabel}</span>
            <span className="question-player__stat-value">{question.points}</span>
          </div>
          <div className="question-player__stat-item">
            <span className="question-player__stat-label">{answeredLabel}</span>
            <span className="question-player__stat-value">{answeredCount}</span>
          </div>
          <div className="question-player__stat-item">
            <span className="question-player__stat-label">{remainingLabel}</span>
            <span className="question-player__stat-value">{remainingCount}</span>
          </div>
          <button
            type="button"
            className="question-player__next"
            disabled={nextButtonDisabled}
            onClick={onNextQuestion}
          >
            {nextButtonLabel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="question-player__next"
          disabled={nextButtonDisabled}
          onClick={onNextQuestion}
        >
          {nextButtonLabel}
        </button>
      )}

      <div className="question-player__content">
        <h2 className="question-player__question">{question.question_text}</h2>
        <p className="question-player__hint">
          {question.scope === 'PODSTAWOWY' ? copy.basicHint : copy.specialistHint}
        </p>

        <div
          className={`question-player__answers ${
            question.type === 'TAK_NIE' ? 'question-player__answers--yes-no' : ''
          }`}
        >
          {answerOptions.map((option) => {
            const isSelected = selectedAnswer === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={!answerControlsEnabled}
                onClick={() => handleAnswerClick(option.value)}
                className={`question-player__answer ${isSelected ? 'question-player__answer--selected' : ''}`}
              >
                {question.type === 'ABC' ? `${option.value}. ${option.label}` : option.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default QuestionPlayer;
